import React, { useRef, useCallback, useEffect, useMemo } from "react";
import { Container, Sprite, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { createButtonPressAnimation } from "../../../../utilities";
import { ASSET_NAME } from "../../../../assets";
import { FONT_CONFIG } from "../../../../textureConfigs";

const BetButton = ({
  x,
  y,
  text,
  onClick,
  active = false,
  disabled = false,
  width,
  height,
  fontSize,
}) => {
  const containerRef = useRef(null);
  const buttonSpriteRef = useRef(null);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // Same as PopiButtonSound

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  const backgroundTexture = PIXI.Assets.get(
    ASSET_NAME.popi_button_bet_background
  );
  const infinityTexture = PIXI.Assets.get(ASSET_NAME.icon_infinity);

  // Check if this is an infinity button
  const isInfinityButton = text === "∞";

  // Calculate infinity icon size maintaining aspect ratio
  const infinityIconSize = useMemo(() => {
    if (!isInfinityButton || !infinityTexture) return { width: 0, height: 0 };

    const iconAspectRatio = infinityTexture.width / infinityTexture.height;
    const maxWidth = width * 0.7;
    const maxHeight = height * 0.9;

    // Calculate size to fit within max dimensions while maintaining aspect ratio
    let iconWidth = maxWidth;
    let iconHeight = iconWidth / iconAspectRatio;

    // If height exceeds max, scale down by height instead
    if (iconHeight > maxHeight) {
      iconHeight = maxHeight;
      iconWidth = iconHeight * iconAspectRatio;
    }

    return { width: iconWidth, height: iconHeight };
  }, [isInfinityButton, infinityTexture, width, height]);

  /**
   * Auto-scaling text font size calculation
   *
   * Ensures text always fits within button width by:
   * 1. Using ideal font size (60% of button height)
   * 2. Measuring text width with ideal font size using PIXI.TextMetrics
   * 3. Comparing against max text width (90% of button width for padding)
   * 4. Only scaling down if text exceeds max width (proportional scaling)
   * 5. Returning ideal font size if text fits (preserves crisp rendering)
   *
   * This prevents text overflow while maintaining sharp text rendering for values
   * that fit naturally. Only long values (e.g., "1.000,00 FUN") get scaled down.
   */
  const finalFontSize = useMemo(() => {
    // Ideal font size - 60% of button height, rounded to even number for crisp rendering
    const idealFontSize = Math.round(height * 0.6);

    // Allow 90% of button width for text (10% padding on sides)
    const maxTextWidth = width * 0.9;

    // Measure text width with ideal font size
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "400",
    });
    const metrics = PIXI.TextMetrics.measureText(text, tempStyle);

    // Only scale down if text exceeds max width
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      // Round to even number for crisp rendering
      return Math.round(idealFontSize * scale);
    }

    // Return ideal font size if it fits (preserves crisp rendering)
    return idealFontSize;
  }, [text, width, height]);

  // Text style for bet value
  const textStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalFontSize,
        fontWeight: "400",
        fill: disabled ? 0x666666 : 0xffffff,
        align: "center",
      }),
    [finalFontSize, disabled]
  );

  // Round text position to whole pixels for crisp rendering
  const textX = Math.round(0);
  const textY = Math.round(0);

  const handleClick = useCallback(() => {
    if (disabled) return;

    // Add press animation
    if (buttonSpriteRef.current) {
      createButtonPressAnimation(buttonSpriteRef.current);
    }

    onClick();
  }, [disabled, onClick]);

  // Apply brightness filter when active
  useEffect(() => {
    if (buttonSpriteRef.current && !disabled) {
      if (active) {
        // Apply brightness filter for active state
        if (
          !buttonSpriteRef.current.filters ||
          buttonSpriteRef.current.filters.length === 0
        ) {
          buttonSpriteRef.current.filters = [brightnessFilter.current];
        }
      } else if (!isHoveredRef.current) {
        // Remove filter if not active and not hovered
        buttonSpriteRef.current.filters = [];
      }
    }
  }, [active, disabled]);

  // Hover effect handlers
  const handlePointerOver = useCallback(() => {
    if (disabled) return;

    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [disabled]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Only remove filter if not active
      if (!active) {
        buttonSpriteRef.current.filters = [];
      }
    }
  }, [active]);

  // Clean up hover effects on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
    };
  }, []);

  return (
    <Container ref={containerRef} name="secondary_bet_button" x={x} y={y}>
      <Sprite
        ref={buttonSpriteRef}
        texture={backgroundTexture}
        name="secondary_bet_button_background"
        anchor={0.5}
        width={width}
        height={height}
        alpha={disabled ? 0.5 : 1}
        eventMode={disabled ? "none" : "static"}
        cursor={disabled ? "default" : "pointer"}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
      />

      {isInfinityButton && infinityTexture ? (
        <Sprite
          texture={infinityTexture}
          anchor={0.5}
          width={infinityIconSize.width}
          height={infinityIconSize.height}
          x={textX}
          y={textY}
          alpha={disabled ? 0.5 : 1}
          eventMode="none"
        />
      ) : (
        <Text
          text={text}
          style={textStyle}
          anchor={0.5}
          x={textX}
          y={textY}
          resolution={2}
          eventMode="none"
        />
      )}
    </Container>
  );
};

export default BetButton;
